import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import EmployeePicker from '../../components/EmployeePicker';
import Pagination from '../../components/Pagination';
import BulkActionBar from '../../components/BulkActionBar';
import SegmentedFilterBar from '../../components/SegmentedFilterBar';
import ScopeToggle from '../../components/ScopeToggle';
import CompanyPicker from '../../components/crm/CompanyPicker';
import ContactPicker from '../../components/crm/ContactPicker';
import LocationCombobox from '../../components/LocationCombobox';
import { useScope } from '../../lib/hooks/useScope';
import { usePaginated } from '../../lib/hooks/usePaginated';
import { useRowSelection } from '../../lib/hooks/useRowSelection';
import { useSortable, SortIcon } from '../../lib/hooks/useSortable';
import { formatDate } from '../../lib/format';
import { apiGet, apiPost, apiPut, apiDel } from '../../lib/api';
import { inrCompact } from '../../lib/format';
import { Plus, Edit2, Trash2, X, ChevronRight, Clock, Download } from 'lucide-react';
import { toast } from 'sonner';
import LeadDetailDrawer from '../../components/crm/LeadDetailDrawer';
import { useFormFieldHelpers } from '../../hooks/useFormFieldHelpers';

// BD_Emergent_Prompt_19Sep2026 (A3): DEFAULT_STAGES is only a fallback for the
// brief window before /config-masters/lead_stages resolves — the governed
// master (Settings → Master Lists) is the real source, fetched into `stages` state.
const DEFAULT_STAGES = ['New Lead', 'Contacted', 'Site Visit', 'BOQ Preparation', 'Tender Submitted', 'Negotiation', 'Won', 'Lost'];
const PRIORITIES = ['Hot', 'Warm', 'Cold'];
const SOURCES = ['Tender', 'Referral', 'Website', 'Direct', 'AI Discovery'];
const PROJECT_TYPES = ['Civil', 'MEP', 'EPC', 'Turnkey', 'Infrastructure'];
const INDUSTRIES = ['Manufacturing', 'Pharma', 'Warehousing', 'Solar', 'Data Center', 'Hospitality', 'Other'];

const initialForm = {
  client: '', contact_person: '', email: '', phone: '', project_type: 'Infrastructure',
  estimated_value: '', source: 'Direct', stage: 'New Lead', priority: 'Warm',
  owner: '', lead_score: '', industry: '', location: '', notes: '',
  company_id: '', contact_id: '', tags: [], custom_fields: {}
};

// Lead Score auto-calc heuristic: priority weight + value weight + stage weight + recency weight
const computeLeadScore = ({ priority, estimated_value, stage }) => {
  let score = 30; // base
  if (priority === 'Hot') score += 30;
  else if (priority === 'Warm') score += 18;
  else if (priority === 'Cold') score += 5;

  const v = Number(estimated_value) || 0;
  if (v >= 100000000) score += 25;        // ≥ 10 Cr
  else if (v >= 50000000) score += 18;    // ≥ 5 Cr
  else if (v >= 10000000) score += 12;    // ≥ 1 Cr
  else if (v >= 1000000) score += 6;      // ≥ 10 L

  const stageBoost = {
    'New Lead': 0, 'Contacted': 4, 'Site Visit': 8,
    'BOQ Preparation': 12, 'Tender Submitted': 16,
    'Negotiation': 20, 'Won': 25, 'Lost': 0
  };
  score += stageBoost[stage] || 0;
  return Math.max(0, Math.min(100, score));
};

const daysSince = (iso) => {
  if (!iso) return null;
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / (1000 * 60 * 60 * 24));
  return diff;
};

export default function LeadTracker() {
  const location = useLocation();
  const navigate = useNavigate();
  const ff = useFormFieldHelpers('lead_create');
  const [leads, setLeads] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [openLeadId, setOpenLeadId] = useState(null);
  const [formData, setFormData] = useState(initialForm);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [stages, setStages] = useState(DEFAULT_STAGES);
  const { scope, setScope } = useScope('leads');

  useEffect(() => {
    apiGet('/config-masters/lead_stages')
      .then((res) => { if (res?.values?.length) setStages(res.values); })
      .catch(() => {});
  }, []);

  // Handle prefill from Companies/Contacts "Push to Lead Tracker"
  useEffect(() => {
    const prefill = location.state?.prefill;
    if (prefill) {
      const legacyLoc = prefill.location || [prefill.city, prefill.state].filter(Boolean).join(', ');
      setFormData({ ...initialForm, ...prefill, location: legacyLoc });
      setShowForm(true);
      navigate(location.pathname, { replace: true, state: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const { sorted, sortKey, sortDir, getProps } = useSortable(filteredLeads, { defaultKey: 'created_at', defaultDir: 'desc' });
  const pagination = usePaginated(sorted, { defaultPageSize: 10 });
  const pageIds = pagination.paged.map((l) => l.id);
  const selection = useRowSelection();

  // Filter definitions for SegmentedFilterBar
  const filterDefs = useMemo(() => {
    const owners = Array.from(new Set(leads.map((l) => l.owner).filter(Boolean))).sort();
    const industries = Array.from(new Set(leads.map((l) => l.industry).filter(Boolean))).sort();
    const locations = Array.from(new Set(leads.map((l) => l.location).filter(Boolean))).sort();
    return [
      { key: 'stage', label: 'Stage', type: 'multiSelect', options: stages },
      { key: 'priority', label: 'Priority', type: 'multiSelect', options: PRIORITIES },
      { key: 'owner', label: 'Owner', type: 'multiSelect', options: owners },
      { key: 'industry', label: 'Industry', type: 'multiSelect', options: industries },
      { key: 'location', label: 'Location', type: 'multiSelect', options: locations },
    ];
  }, [leads, stages]);

  const handleBulkDelete = async () => {
    const ids = Array.from(selection.selected);
    if (!ids.length) return;
    if (!window.confirm(`Delete ${ids.length} selected lead${ids.length === 1 ? '' : 's'}?\n\nThis action cannot be undone.`)) return;
    try {
      await Promise.all(ids.map((id) => apiDel(`/crm/leads/${id}`)));
      toast.success(`Deleted ${ids.length} lead${ids.length === 1 ? '' : 's'}`);
      selection.clear();
      loadLeads();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Bulk delete failed');
    }
  };

  const handleBulkExport = () => {
    const rows = leads.filter((l) => selection.selected.has(l.id));
    const csv = [
      ['Client', 'Contact Person', 'Email', 'Phone', 'Industry', 'Value', 'Stage', 'Priority', 'Owner', 'Location'].join(','),
      ...rows.map((l) => [l.client, l.contact_person, l.email, l.phone, l.industry, l.estimated_value, l.stage, l.priority, l.owner, l.location].map((v) => `"${(v ?? '').toString().replace(/"/g, '""')}"`).join(',')),
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `leads-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} lead${rows.length === 1 ? '' : 's'}`);
  };

  const loadLeads = async () => {
    if (!scope) return;
    try {
      const res = await apiGet(`/crm/leads?scope=${scope}`);
      setLeads(res.leads || []);
    } catch (err) {
      console.error('Failed to load leads:', err);
    }
  };

  useEffect(() => { loadLeads(); /* eslint-disable-next-line */ }, [scope]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.company_id) { toast.error('Please select a Company'); return; }
    if (!formData.contact_id) { toast.error('Please select a Primary Contact'); return; }
    const computedScore = computeLeadScore(formData);
    // Coerce blank numeric fields to null so Pydantic v2 accepts the optional values.
    const ev = (formData.estimated_value === '' || formData.estimated_value == null)
      ? null
      : Number(formData.estimated_value);
    const payload = { ...formData, estimated_value: ev, lead_score: computedScore };
    try {
      if (editingLead) {
        await apiPut(`/crm/leads/${editingLead.id}`, payload);
        toast.success('Lead updated successfully');
      } else {
        await apiPost('/crm/leads', payload);
        toast.success('Lead created successfully');
      }
      setShowForm(false);
      setEditingLead(null);
      setFormData(initialForm);
      loadLeads();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        toast.error(detail[0]?.msg ? `${detail[0].loc?.[1] || ''} ${detail[0].msg}` : 'Validation failed');
      } else {
        toast.error(detail || 'Failed to save lead');
      }
    }
  };

  const openEditFromDrawer = (lead) => {
    setEditingLead(lead);
    // Migrate legacy "city + state" pair into a single location string.
    const legacyLoc = lead.location || [lead.city, lead.state].filter(Boolean).join(', ');
    setFormData({ ...initialForm, ...lead, location: legacyLoc });
    setOpenLeadId(null);
    setShowForm(true);
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this lead?\n\nThis action cannot be undone.')) return;
    try {
      await apiDel(`/crm/leads/${id}`);
      toast.success('Lead deleted');
      loadLeads();
    } catch (err) {
      toast.error('Failed to delete lead');
    }
  };

  const priorityColor = (p) => {
    if (p === 'Hot') return 'bg-red-100 text-red-700';
    if (p === 'Warm') return 'bg-amber-100 text-amber-700';
    return 'bg-slate-100 text-slate-700';
  };

  const stageColor = (s) => {
    if (s === 'Won') return 'bg-emerald-100 text-emerald-700';
    if (s === 'Lost') return 'bg-red-100 text-red-700';
    return 'bg-blue-100 text-blue-700';
  };

  const lastContactedLabel = (lead) => {
    const d = daysSince(lead.last_contacted);
    if (d === null) return <span className="text-red-600">Never</span>;
    if (d === 0) return <span className="text-emerald-700">Today</span>;
    if (d === 1) return <span className="text-emerald-700">1d ago</span>;
    if (d <= 7) return <span className="text-amber-700">{d}d ago</span>;
    return <span className="text-red-700">{d}d ago</span>;
  };

  return (
    <div>
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#3b4872] mb-2">Business Development</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Lead Tracker</h1>
          <p className="text-sm text-slate-600 mt-1">Click any lead to view its full activity timeline and log new interactions</p>
        </div>
        <button onClick={() => setShowForm(true)} className="btn-primary flex items-center gap-2" data-testid="add-lead-btn">
          <Plus size={16} /> Add Lead
        </button>
      </div>

      {/* Mine · My Team · All scope toggle (visible only to managers) */}
      <div className="mb-3 flex items-center justify-between">
        <ScopeToggle value={scope} onChange={setScope} testid="scope-toggle-leads" />
        <div className="text-xs text-slate-500">
          Showing <b className="text-[#3b4872]">{leads.length}</b> leads · <span className="uppercase tracking-wider font-display font-bold text-[10px]">{scope}</span>
        </div>
      </div>

      {/* Rich filters + saved segments */}
      <SegmentedFilterBar
        entityType="leads"
        items={leads.map((l) => ({ ...l, location: l.location || [l.city, l.state].filter(Boolean).join(', ') }))}
        filterDefs={filterDefs}
        onFiltered={setFilteredLeads}
      />
      <div className="mb-4"></div>

      {/* Bulk-action bar (appears only when rows selected) */}
      <BulkActionBar count={selection.count} onClear={selection.clear} className="mb-3">
        <button onClick={handleBulkExport} className="text-xs font-display font-bold uppercase tracking-wider px-3 py-1.5 bg-white/15 text-white rounded-sm hover:bg-white/25 flex items-center gap-1" data-testid="bulk-export">
          <Download size={11} /> Export CSV
        </button>
        <button onClick={handleBulkDelete} className="text-xs font-display font-bold uppercase tracking-wider px-3 py-1.5 bg-red-600 text-white rounded-sm hover:bg-red-700 flex items-center gap-1" data-testid="bulk-delete">
          <Trash2 size={11} /> Delete
        </button>
      </BulkActionBar>

      {/* Leads Table */}
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="leads-table">
        <table className="w-full dense-table">
          <thead>
            <tr>
              <th className="w-10">
                <input
                  type="checkbox"
                  className="w-4 h-4 accent-[#3b4872] cursor-pointer"
                  checked={selection.allSelected(pageIds)}
                  ref={(el) => { if (el) el.indeterminate = selection.someSelected(pageIds); }}
                  onChange={() => selection.toggleAll(pageIds)}
                  data-testid="select-all-leads"
                />
              </th>
              <th {...getProps('client')}>Client <SortIcon dir={sortKey === 'client' ? sortDir : null} /></th>
              <th {...getProps('contact_person')}>Contact <SortIcon dir={sortKey === 'contact_person' ? sortDir : null} /></th>
              <th {...getProps('industry')}>Industry <SortIcon dir={sortKey === 'industry' ? sortDir : null} /></th>
              <th {...getProps('estimated_value')}>Value <SortIcon dir={sortKey === 'estimated_value' ? sortDir : null} /></th>
              <th {...getProps('stage')}>Stage <SortIcon dir={sortKey === 'stage' ? sortDir : null} /></th>
              <th {...getProps('priority')}>Priority <SortIcon dir={sortKey === 'priority' ? sortDir : null} /></th>
              <th {...getProps('owner')}>Owner <SortIcon dir={sortKey === 'owner' ? sortDir : null} /></th>
              <th>Last Contacted</th>
              <th>Tasks</th>
              <th {...getProps('created_at')}>Created <SortIcon dir={sortKey === 'created_at' ? sortDir : null} /></th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {pagination.paged.map(lead => (
              <tr
                key={lead.id}
                className={`cursor-pointer hover:bg-slate-50 transition-colors ${selection.isSelected(lead.id) ? 'bg-indigo-50/40' : ''}`}
                onClick={() => setOpenLeadId(lead.id)}
                data-testid={`lead-row-${lead.id}`}
              >
                <td onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    className="w-4 h-4 accent-[#3b4872] cursor-pointer"
                    checked={selection.isSelected(lead.id)}
                    onChange={() => selection.toggle(lead.id)}
                    data-testid={`select-lead-${lead.id}`}
                  />
                </td>
                <td>
                  <div className="font-display font-semibold text-[#0F172A] flex items-center gap-2">
                    {lead.client}
                  </div>
                  <div className="text-xs text-slate-500">{lead.location}</div>
                </td>
                <td className="text-xs">
                  <div>{lead.contact_person || '-'}</div>
                  <div className="text-slate-500">{lead.email || lead.phone || '-'}</div>
                </td>
                <td className="text-xs">{lead.industry || '-'}</td>
                <td className="font-display font-bold text-xs">{lead.estimated_value ? inrCompact(lead.estimated_value) : '-'}</td>
                <td><span className={`tag ${stageColor(lead.stage)}`}>{lead.stage}</span></td>
                <td><span className={`tag ${priorityColor(lead.priority)}`}>{lead.priority}</span></td>
                <td className="text-xs">{lead.owner}</td>
                <td className="text-xs font-semibold">{lastContactedLabel(lead)}</td>
                <td className="text-xs">
                  {lead.open_tasks_count > 0 ? (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-indigo-50 text-[#3b4872] rounded-sm font-semibold">
                      <Clock size={11} /> {lead.open_tasks_count}
                    </span>
                  ) : <span className="text-slate-400">—</span>}
                </td>
                <td className="text-xs whitespace-nowrap">{formatDate(lead.created_at)}</td>
                <td onClick={e => e.stopPropagation()}>
                  <div className="flex gap-2 items-center">
                    <button onClick={(e) => handleDelete(lead.id, e)} className="text-red-600 hover:text-red-700" data-testid={`delete-lead-${lead.id}`}><Trash2 size={14} /></button>
                    <ChevronRight size={14} className="text-slate-400" />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {leads.length === 0 && (
          <div className="p-8 text-center text-sm text-slate-500">No leads found. Create your first lead to get started.</div>
        )}
        {leads.length > 0 && (
          <div className="border-t border-slate-200 px-4 py-3">
            <Pagination
              page={pagination.page}
              pageSize={pagination.pageSize}
              total={pagination.total}
              start={pagination.start}
              end={pagination.end}
              onPageChange={pagination.setPage}
              onPageSizeChange={pagination.setPageSize}
              label="leads"
            />
          </div>
        )}
      </div>

      {/* Detail Drawer */}
      {openLeadId && (
        <LeadDetailDrawer
          entityType="lead"
          entityId={openLeadId}
          onClose={() => { setOpenLeadId(null); loadLeads(); }}
          onUpdated={loadLeads}
          onEdit={openEditFromDrawer}
        />
      )}

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-sm shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-slate-200 p-6 flex items-center justify-between">
              <h2 className="font-display text-xl font-bold">{editingLead ? 'Edit Lead' : 'Add New Lead'}</h2>
              <button onClick={() => { setShowForm(false); setEditingLead(null); }} className="text-slate-500 hover:text-slate-700"><X size={20} /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="label">Company *</label>
                  <CompanyPicker
                    value={formData.company_id}
                    onChange={(id, obj) => setFormData({
                      ...formData,
                      company_id: id,
                      client: obj?.name || '',
                      contact_id: '',
                      contact_person: '', email: '', phone: '',
                      industry: obj?.industry || formData.industry,
                      location: obj?.location || (obj && (obj.city || obj.state) ? [obj.city, obj.state].filter(Boolean).join(', ') : formData.location),
                    })}
                  />
                </div>
                <div className="col-span-2">
                  <label className="label">Primary Contact *</label>
                  <ContactPicker
                    value={formData.contact_id}
                    companyId={formData.company_id || null}
                    onChange={(id, obj) => setFormData({
                      ...formData,
                      contact_id: id,
                      contact_person: obj?.name || '',
                      email: obj?.email || '',
                      phone: obj?.phone || '',
                    })}
                    required
                  />
                  <p className="text-[11px] text-slate-500 mt-1">Pick the primary point of contact for this lead. Their name, email & phone auto-populate.</p>
                </div>
                <div><label className="label">Project Type *</label><select value={formData.project_type} onChange={(e) => setFormData({...formData, project_type: e.target.value})} className="input" required>{PROJECT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}</select></div>
                <div><label className="label">Industry</label><select value={formData.industry} onChange={(e) => setFormData({...formData, industry: e.target.value})} className="input" data-testid="lead-form-industry"><option value="">—</option>{INDUSTRIES.map(i => <option key={i} value={i}>{i}</option>)}</select></div>
                {ff.showField('estimated_value') && <div><label className="label">{ff.labelOf('estimated_value','Estimated Value (₹)')}</label><input type="number" value={formData.estimated_value} onChange={(e) => setFormData({...formData, estimated_value: e.target.value})} className="input" placeholder="Optional" /></div>}
                <div>
                  <label className="label">Lead Score <span className="text-[10px] uppercase tracking-wider text-slate-500 ml-1">(auto)</span></label>
                  <input
                    type="number"
                    value={computeLeadScore(formData)}
                    readOnly
                    className="input bg-slate-50 cursor-not-allowed"
                    data-testid="lead-form-score-auto"
                  />
                </div>
                <div className="col-span-2">
                  <label className="label">Location</label>
                  <LocationCombobox value={formData.location} onChange={(v) => setFormData({ ...formData, location: v })} testid="lead-location" />
                </div>
                <div><label className="label">{ff.labelOf('source','Source')}{ff.reqStar('source', true)}</label><select value={formData.source} onChange={(e) => setFormData({...formData, source: e.target.value})} className="input" required={ff.isReq('source', true)}>{SOURCES.map(s => <option key={s} value={s}>{s}</option>)}</select></div>
                <div><label className="label">{ff.labelOf('stage','Stage')}{ff.reqStar('stage', true)}</label><select value={formData.stage} onChange={(e) => setFormData({...formData, stage: e.target.value})} className="input" required={ff.isReq('stage', true)}>{stages.map(s => <option key={s} value={s}>{s}</option>)}</select></div>
                <div><label className="label">Priority *</label><select value={formData.priority} onChange={(e) => setFormData({...formData, priority: e.target.value})} className="input" required>{PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}</select></div>
                <div><label className="label">Lead Owner *</label><EmployeePicker value={formData.owner} onChange={(v) => setFormData({...formData, owner: v})} testid="lead-owner-picker" required restrictToTeam /></div>
              </div>
              {ff.showField('notes') && <div><label className="label">{ff.labelOf('notes','Notes')}{ff.reqStar('notes', false)}</label><textarea value={formData.notes} onChange={(e) => setFormData({...formData, notes: e.target.value})} required={ff.isReq('notes', false)} className="input" rows={3} placeholder="Additional information..." /></div>}
              <div className="flex gap-3 justify-end pt-4">
                <button type="button" onClick={() => { setShowForm(false); setEditingLead(null); }} className="btn-secondary">Cancel</button>
                <button type="submit" className="btn-primary" data-testid="lead-form-submit">{editingLead ? 'Update' : 'Create'} Lead</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
